import React from "react";
import { Link } from "react-router-dom";

export default function Privacy() {
    return (
        <div className="max-w-3xl mx-auto px-6 lg:px-10 py-16" data-testid="privacy-page">
            <p className="text-xs uppercase tracking-widest text-primary mb-3">Legal</p>
            <h1 className="font-display text-4xl md:text-5xl text-accent tracking-tight mb-3">Privacy Policy</h1>
            <p className="text-sm text-muted-foreground mb-10">Last updated: February 14, 2026</p>

            <div className="prose prose-invert max-w-none space-y-6 text-muted-foreground leading-relaxed">
                <section>
                    <h2 className="text-accent font-display text-2xl mt-8 mb-2">1. What we collect</h2>
                    <ul className="list-disc list-inside space-y-1 mt-2">
                        <li><strong className="text-accent">Account info</strong>: email and, if you use Google sign-in, your name and profile picture.</li>
                        <li><strong className="text-accent">Fact-check inputs</strong>: text, images, or audio you submit — stored so you can review your history and to power the Trending feed. Trending items are anonymized.</li>
                        <li><strong className="text-accent">Payment info</strong>: donations, Pro subscriptions, and merch pre-orders are processed by Stripe. We store only the Stripe session ID and amount — we never see your card number.</li>
                        <li><strong className="text-accent">Analytics</strong>: Google Analytics 4 (with IP anonymization) and PostHog for aggregate page views, feature usage, and error tracking. No personally identifiable content from your fact-checks is sent to analytics.</li>
                        <li><strong className="text-accent">Ads</strong>: when Google AdSense is active, Google may set cookies to serve relevant ads. Pro subscribers see no ads.</li>
                    </ul>
                </section>

                <section>
                    <h2 className="text-accent font-display text-2xl mt-8 mb-2">2. AI providers</h2>
                    <p>
                        Your fact-check inputs are sent to OpenAI (for GPT-5.2 text, GPT-5.2 vision, and Whisper transcription) via the Emergent
                        LLM proxy. These providers may retain requests for abuse monitoring per their own privacy policies. Do not submit content
                        you would not be willing to share with a third-party AI service.
                    </p>
                </section>

                <section>
                    <h2 className="text-accent font-display text-2xl mt-8 mb-2">3. How we use your data</h2>
                    <ul className="list-disc list-inside space-y-1 mt-2">
                        <li>To provide fact-check verdicts, scam detection, saved history, and Pro features.</li>
                        <li>To process donations and merch orders through Stripe.</li>
                        <li>To send you receipts, Pro welcome emails, order confirmations, and — if you opt in — the newsletter and Fact of the Day.</li>
                        <li>To improve the product through anonymized, aggregated analytics.</li>
                        <li>To detect and prevent abuse (rate-limiting anonymous users by IP).</li>
                    </ul>
                </section>

                <section>
                    <h2 className="text-accent font-display text-2xl mt-8 mb-2">4. What we do NOT do</h2>
                    <ul className="list-disc list-inside space-y-1 mt-2">
                        <li>We do not sell your personal data.</li>
                        <li>We do not share your fact-check content with third-party marketers.</li>
                        <li>We do not train our own AI models on your data.</li>
                    </ul>
                </section>

                <section>
                    <h2 className="text-accent font-display text-2xl mt-8 mb-2">5. Cookies</h2>
                    <p>
                        We use a session cookie for login and localStorage for onboarding preferences and theme selection. Google Analytics and
                        AdSense (when active) set their own cookies. You can block cookies in your browser settings; the site will still work but
                        some features (login, saved theme) may not persist.
                    </p>
                </section>

                <section>
                    <h2 className="text-accent font-display text-2xl mt-8 mb-2">6. Your rights</h2>
                    <p>
                        You can:
                    </p>
                    <ul className="list-disc list-inside space-y-1 mt-2">
                        <li>Request a copy of the data we hold about you.</li>
                        <li>Delete your account and all associated fact-check history.</li>
                        <li>Unsubscribe from any email at any time (link at the bottom of every email).</li>
                        <li>Opt out of AdSense personalization at <a href="https://adssettings.google.com" className="text-primary underline" target="_blank" rel="noreferrer">adssettings.google.com</a>.</li>
                    </ul>
                    <p>Email <a href="mailto:privacy@real-check.org" className="text-primary underline">privacy@real-check.org</a> to exercise any of these rights.</p>
                </section>

                <section>
                    <h2 className="text-accent font-display text-2xl mt-8 mb-2">7. Children</h2>
                    <p>Real-Check is not directed at children under 13. We do not knowingly collect data from anyone under 13.</p>
                </section>

                <section>
                    <h2 className="text-accent font-display text-2xl mt-8 mb-2">8. Changes</h2>
                    <p>
                        We may update this policy. Material changes will be announced on the homepage or by email. See our{" "}
                        <Link to="/terms" className="text-primary underline">Terms of Service</Link>.
                    </p>
                </section>

                <section>
                    <h2 className="text-accent font-display text-2xl mt-8 mb-2">9. Contact</h2>
                    <p>
                        Privacy questions? Email <a href="mailto:privacy@real-check.org" className="text-primary underline">privacy@real-check.org</a>.
                    </p>
                </section>
            </div>
        </div>
    );
}
